/**
 * useCrossDomainSession
 * 
 * Detects ?session_token= in URL on app mount.
 * Exchanges it for a session via the exchange-session-token edge function.
 * Cleans the URL param after processing.
 * Skips if user already has an active session.
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { USER_LOCALE_STORAGE_KEY } from '@/constants/routing';
import { isDevDomain } from '@/constants/branding';
import { setCrossDomainHandshakeActive } from '@/lib/crossDomainHandshake';
import {
  CROSS_DOMAIN_LOCALE_PARAM,
  CROSS_DOMAIN_LOCATION_PARAM,
  persistCrossDomainOverrides,
} from '@/lib/crossDomainState';
import { logger } from '@/utils/logger';

export function useCrossDomainSession(): void {
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;

    const hostname = window.location.hostname;
    const isDev = isDevDomain();
    logger.info('[CrossDomain] Init check', { hostname, isDev });

    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('session_token');
    const handoffLocale = params.get(CROSS_DOMAIN_LOCALE_PARAM);
    const handoffLocation = params.get(CROSS_DOMAIN_LOCATION_PARAM);

    if (!sessionToken && !handoffLocale && !handoffLocation) return;

    processed.current = true;
    setCrossDomainHandshakeActive(true);
    logger.info('[CrossDomain] Handoff detected, cleaning URL', {
      hasSessionToken: Boolean(sessionToken),
      handoffLocale,
      handoffLocation,
    });

    // Persist immediately so parent providers can consume the override even if
    // they initialize after this effect has already cleaned the URL.
    persistCrossDomainOverrides(handoffLocale, handoffLocation);

    // If landing on tributario.com.br, force pt-BR locale in localStorage
    // to prevent DomainEnforcer from seeing a stale non-PT locale and
    // bouncing back to tax.network before the handshake completes.
    if (hostname === 'tributario.com.br' || hostname.endsWith('.tributario.com.br')) {
      try {
        localStorage.setItem(USER_LOCALE_STORAGE_KEY, handoffLocale || 'pt-BR');
        logger.info('[CrossDomain] Forced pt-BR locale for tributario.com.br');
      } catch {
        // Safari iframe may block storage — non-fatal
      }
    } else if (handoffLocale) {
      try {
        localStorage.setItem(USER_LOCALE_STORAGE_KEY, handoffLocale);
      } catch {
        // Storage unavailable — non-fatal
      }
    }

    // Clean the URL immediately (remove handoff params)
    params.delete('session_token');
    params.delete(CROSS_DOMAIN_LOCALE_PARAM);
    params.delete(CROSS_DOMAIN_LOCATION_PARAM);
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, '', cleanUrl);

    // On dev/Lovable preview domains, the fetch proxy breaks Supabase auth calls.
    // Clean the URL param but skip the actual token exchange.
    if (isDev) {
      logger.warn('[CrossDomain] Dev/Lovable domain detected — skipping token exchange (fetch proxy incompatible)');
      setCrossDomainHandshakeActive(false);
      return;
    }

    // Exchange the token for a session
    (async () => {
      let shouldClearFlag = true;
      try {
        // Skip if user already has an active session
        const { data: existingSession } = await supabase.auth.getSession();
        if (existingSession?.session) {
          logger.info('[CrossDomain] User already has active session, skipping token exchange');
          setCrossDomainHandshakeActive(false);
          return;
        }

        if (!sessionToken) {
          logger.info('[CrossDomain] No session token present, state-only handoff complete');
          return;
        }

        const { data, error } = await supabase.functions.invoke('exchange-session-token', {
          body: {
            token: sessionToken,
            redirect_to: window.location.origin,
          },
        });

        if (error || !data?.success) {
          logger.warn('[CrossDomain] Token exchange failed', { error: error?.message || data?.error });
          return;
        }

        const { hashed_token, verification_type, email } = data.data;

        if (!hashed_token || !email) {
          logger.warn('[CrossDomain] Missing verification data');
          return;
        }

        // Verify the OTP to establish a local session
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: verification_type || 'magiclink',
          token_hash: hashed_token,
        });

        if (verifyError) {
          logger.warn('[CrossDomain] OTP verification failed', { error: verifyError.message });
          return;
        }

        logger.info('[CrossDomain] Session established from cross-domain token');

        // Re-persist before reload so the destination providers can consume the
        // same locale/location override on the fresh post-auth mount.
        persistCrossDomainOverrides(handoffLocale, handoffLocation);
        
        // Reload so all auth-dependent components re-initialize with the new session
        window.location.reload();
      } catch (err) {
        logger.warn('[CrossDomain] Unexpected error during token exchange', { error: err });
      } finally {
        if (shouldClearFlag) {
          setCrossDomainHandshakeActive(false);
        }
      }
    })();
  }, []);
}
